import { useEffect, useMemo, useState } from "react";
import {
    MapPin,
    Mail,
    Building2,
    Search,
    SlidersHorizontal,
    CheckCircle2,
    Clock,
    PauseCircle,
    Loader2,
    AlertCircle,
} from "lucide-react";
import { getCenters } from "../service/userService";

const statusStyle = {
    Active: "bg-green-100 text-green-600",
    Onboarding: "bg-orange-100 text-orange-500",
    Inactive: "bg-slate-100 text-slate-500",
};

const statusOptions = ["All Status", "Active", "Onboarding", "Inactive"];

function StatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-slate-900">{value}</p>
                <p className="text-sm text-slate-500">{label}</p>
            </div>
        </div>
    );
}

// Transforms a raw facility record from the API into the shape the table expects
function mapFacilityToCentre(facility) {
    return {
        id: facility.id,
        name: facility.facilityName || "Unnamed Facility",
        org: facility.vendor?.businessName || facility.vendor?.name || "—",
        email: facility.vendor?.email || "—",
        location: facility.city || "—",
        regNo: `FAC-${String(facility.id).padStart(4, "0")}`,
        status: facility.active ? "Active" : "Inactive",
    };
}

export default function CentresTable() {
    const [centres, setCentres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [query, setQuery] = useState("");
    const [status, setStatus] = useState("All Status");
    const [location, setLocation] = useState("All Locations");

    useEffect(() => {
        const fetchFacilities = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await getCenters();

                // API returns a raw array (as in your sample), but this also
                // safely handles a { data: [...] } wrapped shape if that ever changes
                const rawList = Array.isArray(response) ? response : response?.data || [];

                const mapped = rawList.map(mapFacilityToCentre);
                setCentres(mapped);
            } catch (err) {
                console.error("Failed to fetch facilities:", err);
                setError(
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to load centres. Please try again."
                );
            } finally {
                setLoading(false);
            }
        };

        fetchFacilities();
    }, []);

    const locationOptions = useMemo(
        () => ["All Locations", ...new Set(centres.map((c) => c.location))],
        [centres]
    );

    const counts = useMemo(() => {
        const active = centres.filter((c) => c.status === "Active").length;
        const onboarding = centres.filter((c) => c.status === "Onboarding").length;
        const inactive = centres.filter((c) => c.status === "Inactive").length;
        return { total: centres.length, active, onboarding, inactive };
    }, [centres]);

    const filtered = useMemo(() => {
        return centres.filter((c) => {
            const matchesQuery =
                query.trim() === "" ||
                c.name.toLowerCase().includes(query.toLowerCase()) ||
                c.org.toLowerCase().includes(query.toLowerCase()) ||
                c.email.toLowerCase().includes(query.toLowerCase());

            const matchesStatus = status === "All Status" || c.status === status;
            const matchesLocation = location === "All Locations" || c.location === location;

            return matchesQuery && matchesStatus && matchesLocation;
        });
    }, [centres, query, status, location]);

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-slate-900">Centres</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        All fabrication and prototyping centres registered across Andhra Pradesh
                    </p>
                </div>

                {error && (
                    <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                        <AlertCircle size={20} />
                        <span>{error}</span>
                    </div>
                )}

                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={Building2} label="Total centres" value={loading ? "—" : counts.total} tone="bg-indigo-50 text-indigo-500" />
                    <StatCard icon={CheckCircle2} label="Active" value={loading ? "—" : counts.active} tone="bg-green-50 text-green-500" />
                    <StatCard icon={Clock} label="Onboarding" value={loading ? "—" : counts.onboarding} tone="bg-orange-50 text-orange-500" />
                    <StatCard icon={PauseCircle} label="Inactive" value={loading ? "—" : counts.inactive} tone="bg-slate-100 text-slate-500" />
                </div>

                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-6 border-b border-slate-100">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">
                                Registered Centres
                            </h2>
                            <p className="text-slate-500 text-sm mt-0.5">
                                {loading ? "Loading centres..." : `${filtered.length} of ${centres.length} centres across Andhra Pradesh`}
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <div className="relative">
                                <Search
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search name, org, email..."
                                    disabled={loading}
                                    className="pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition w-full sm:w-64 disabled:opacity-50"
                                />
                            </div>

                            <select
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                disabled={loading}
                                className="text-sm rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition disabled:opacity-50"
                            >
                                {locationOptions.map((loc) => (
                                    <option key={loc} value={loc}>
                                        {loc}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                disabled={loading}
                                className="text-sm rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition disabled:opacity-50"
                            >
                                {statusOptions.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>

                            {(query || status !== "All Status" || location !== "All Locations") && (
                                <button
                                    onClick={() => {
                                        setQuery("");
                                        setStatus("All Status");
                                        setLocation("All Locations");
                                    }}
                                    className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 px-3 py-2 transition"
                                >
                                    <SlidersHorizontal size={14} />
                                    Reset
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
                        <table className="w-full min-w-[860px] text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 bg-slate-50">
                                    <th className="px-6 py-3 font-semibold">Centre Name</th>
                                    <th className="px-6 py-3 font-semibold">Organization</th>
                                    <th className="px-6 py-3 font-semibold">Email</th>
                                    <th className="px-6 py-3 font-semibold">Location</th>
                                    {/* <th className="px-6 py-3 font-semibold">Registration No.</th> */}
                                    <th className="px-6 py-3 font-semibold">Status</th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-slate-100">
                                {loading && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 size={18} className="animate-spin" />
                                                Loading centres...
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {!loading && filtered.map((centre) => (
                                    <tr
                                        key={centre.id}
                                        className="hover:bg-slate-50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                                                    <Building2 size={16} />
                                                </div>
                                                <span className="font-semibold text-slate-900">
                                                    {centre.name}
                                                </span>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 text-slate-600">
                                            {centre.org}
                                        </td>

                                        <td className="px-6 py-4 text-slate-600">
                                            <div className="flex items-center gap-1.5">
                                                <Mail size={14} className="text-slate-400" />
                                                {centre.email}
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 text-slate-600">
                                            <div className="flex items-center gap-1.5">
                                                <MapPin size={14} className="text-slate-400" />
                                                {centre.location}
                                            </div>
                                        </td>

                                        {/* <td className="px-6 py-4 text-slate-500">
                                            {centre.regNo}
                                        </td> */}

                                        <td className="px-6 py-4">
                                            <span
                                                className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyle[centre.status]}`}
                                            >
                                                {centre.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}

                                {!loading && !error && filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                                            No centres match your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}